import { useCallback, useEffect, useMemo, useState } from "react";
import { ChevronDown, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { apiGetJson, apiSendJson } from "@/lib/sm-api";
import { AdminGlassPanel } from "./AdminGlassPanel";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";

interface EnvRow {
  key: string;
  configured: boolean;
  contexts: string[];
  scopes: string[];
  domain: string;
  critical: boolean | null;
}

const SENSITIVE_RE = /(SECRET|TOKEN|PASSWORD|PRIVATE|DATABASE_URL|JWT|ADMIN_KEY|API_KEY|CLIENT_SECRET|ACCESS_KEY)/i;

function fmtSyncedAt(iso: string) {
  return new Date(iso).toLocaleString("es-AR", { dateStyle: "short", timeStyle: "short" });
}

export function NetlifyEnvCenterPanel() {
  const [rows, setRows] = useState<EnvRow[]>([]);
  const [missingRequired, setMissingRequired] = useState<string[]>([]);
  const [syncedAt, setSyncedAt] = useState<string | null>(null);
  const [notice, setNotice] = useState("");
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [domainFilter, setDomainFilter] = useState("all");
  const [problemsOnly, setProblemsOnly] = useState(false);
  const [collapsed, setCollapsed] = useState<Record<string, boolean>>({});

  const [formOpen, setFormOpen] = useState(false);
  const [editKey, setEditKey] = useState("");
  const [editValue, setEditValue] = useState("");
  const [context, setContext] = useState("production");
  const [busy, setBusy] = useState(false);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [pendingAction, setPendingAction] = useState<"create" | "update" | "delete">("update");

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await apiGetJson<{
        variables: EnvRow[];
        notice?: string;
        missingRequired?: string[];
        syncedAt?: string;
      }>("/api/admin/netlify-env");
      setRows(r.variables ?? []);
      setMissingRequired(r.missingRequired ?? []);
      setSyncedAt(r.syncedAt ?? null);
      setNotice(r.notice ?? "");
    } catch (e) {
      toast.error(e instanceof Error ? e.message : "No se pudieron listar las variables");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  const domains = useMemo(() => {
    const set = new Set(rows.map((r) => r.domain));
    return Array.from(set).sort();
  }, [rows]);

  const visible = useMemo(() => {
    const q = search.trim().toLowerCase();
    return rows.filter((r) => {
      if (q && !r.key.toLowerCase().includes(q)) return false;
      if (domainFilter !== "all" && r.domain !== domainFilter) return false;
      if (problemsOnly && r.configured) return false;
      return true;
    });
  }, [rows, search, domainFilter, problemsOnly]);

  const grouped = useMemo(() => {
    const map = new Map<string, EnvRow[]>();
    for (const r of visible) {
      if (!map.has(r.domain)) map.set(r.domain, []);
      map.get(r.domain)!.push(r);
    }
    return Array.from(map.entries()).sort((a, b) => a[0].localeCompare(b[0]));
  }, [visible]);

  const kpis = useMemo(
    () => ({
      total: rows.length,
      configured: rows.filter((r) => r.configured).length,
      missing: missingRequired.length,
      domains: domains.length,
    }),
    [rows, missingRequired, domains],
  );

  function openCreate() {
    setEditKey("");
    setEditValue("");
    setContext("production");
    setFormOpen(true);
  }

  function openEdit(key: string) {
    setEditKey(key);
    setEditValue("");
    setContext("production");
    setFormOpen(true);
  }

  const run = async (action: "create" | "update" | "delete", confirmSensitive = false) => {
    if (!editKey.trim()) {
      toast.error("Indicá el nombre de la variable");
      return;
    }
    if (action !== "delete" && !editValue) {
      toast.error("Indicá el nuevo valor");
      return;
    }
    if (action !== "delete" && SENSITIVE_RE.test(editKey) && !confirmSensitive) {
      setPendingAction(action);
      setConfirmOpen(true);
      return;
    }
    setBusy(true);
    try {
      const r = await apiSendJson<{ ok?: boolean; notice?: string; error?: string; code?: string }>(
        "/api/admin/netlify-env",
        "POST",
        {
          action,
          key: editKey.trim(),
          value: action === "delete" ? undefined : editValue,
          context,
          confirmSensitive: confirmSensitive || !SENSITIVE_RE.test(editKey),
        },
      );
      toast.success(r.notice ?? "Guardado en Netlify. Se aplicará en el próximo deploy.");
      setEditValue("");
      setFormOpen(false);
      await load();
    } catch (e) {
      toast.error(e instanceof Error ? e.message : "No se pudo guardar");
    } finally {
      setBusy(false);
      setConfirmOpen(false);
    }
  };

  return (
    <AdminGlassPanel
      title="Environment Control Center"
      description="Variables reales de Netlify, agrupadas por integración. Los cambios se guardan ahí y se aplican en el próximo deploy."
    >
      <p className="rounded-md border border-amber-500/20 bg-amber-500/10 px-3 py-2 text-xs text-amber-200">
        {notice || "El cambio está guardado en Netlify y se aplicará al próximo deploy. No queda activo de inmediato."}
      </p>

      <div className="flex flex-wrap items-center justify-between gap-2">
        <p className="text-xs text-muted-foreground">
          Fuente: Netlify {syncedAt ? `· Sincronizado ${fmtSyncedAt(syncedAt)}` : ""}
        </p>
        <div className="flex gap-2">
          <Button size="sm" variant="outline" className="h-8 text-xs" onClick={() => void load()} disabled={loading}>
            {loading ? <Loader2 className="h-3 w-3 animate-spin" /> : "Actualizar"}
          </Button>
          <Button size="sm" className="h-8 text-xs" onClick={openCreate}>
            Nueva variable
          </Button>
        </div>
      </div>

      <div className="grid grid-cols-2 gap-2 sm:grid-cols-4">
        {[
          { label: "Total", value: kpis.total },
          { label: "Configuradas", value: kpis.configured },
          { label: "Faltantes (requeridas)", value: kpis.missing },
          { label: "Integraciones", value: kpis.domains },
        ].map((k) => (
          <div key={k.label} className="rounded-lg border border-border/60 p-2">
            <p className="text-[10px] text-muted-foreground">{k.label}</p>
            <p className="text-lg font-semibold tabular-nums">{k.value}</p>
          </div>
        ))}
      </div>

      {missingRequired.length > 0 && (
        <div className="rounded-md border border-red-500/30 bg-red-500/10 px-3 py-2 text-xs text-red-300">
          Faltan variables requeridas: {missingRequired.join(", ")}
        </div>
      )}

      <div className="flex flex-wrap gap-2">
        <Input className="h-8 max-w-xs text-xs" placeholder="Buscar variable…" value={search} onChange={(e) => setSearch(e.target.value)} />
        <select
          className="h-8 rounded-md border border-input bg-background px-2 text-xs"
          value={domainFilter}
          onChange={(e) => setDomainFilter(e.target.value)}
        >
          <option value="all">Todas las integraciones</option>
          {domains.map((d) => (
            <option key={d} value={d}>
              {d}
            </option>
          ))}
        </select>
        <label className="flex items-center gap-1.5 text-xs text-muted-foreground">
          <input type="checkbox" checked={problemsOnly} onChange={(e) => setProblemsOnly(e.target.checked)} />
          Solo problemas
        </label>
      </div>

      <div className="space-y-2">
        {grouped.length === 0 ? (
          <p className="py-6 text-center text-xs text-muted-foreground">Sin variables que coincidan con el filtro.</p>
        ) : (
          grouped.map(([domain, items]) => {
            const isCollapsed = collapsed[domain];
            const configuredCount = items.filter((i) => i.configured).length;
            return (
              <div key={domain} className="rounded-lg border border-border/60">
                <button
                  className="flex w-full items-center justify-between px-3 py-2 text-left text-xs font-medium"
                  onClick={() => setCollapsed((prev) => ({ ...prev, [domain]: !prev[domain] }))}
                >
                  <span>
                    {domain} <span className="text-muted-foreground">({configuredCount}/{items.length} configuradas)</span>
                  </span>
                  <ChevronDown className={`h-3.5 w-3.5 transition-transform ${isCollapsed ? "" : "rotate-180"}`} />
                </button>
                {!isCollapsed && (
                  <div className="divide-y divide-border/40 border-t border-border/60">
                    {items.map((row) => (
                      <div key={row.key} className="flex items-center justify-between gap-2 px-3 py-2 text-xs">
                        <div className="min-w-0">
                          <div className="flex items-center gap-1.5">
                            <span className="truncate font-mono">{row.key}</span>
                            {row.critical === true && !row.configured && (
                              <span className="rounded-full bg-red-500/15 px-1.5 py-0.5 text-[10px] text-red-400">crítica</span>
                            )}
                          </div>
                          <p className="text-[11px] text-muted-foreground">
                            {row.configured ? "Configurada · ••••••••••••" : "No configurada"}
                            {row.contexts.length > 0 ? ` · ${row.contexts.join(", ")}` : ""}
                          </p>
                        </div>
                        <Button size="sm" variant="ghost" className="h-7 shrink-0 text-[11px]" onClick={() => openEdit(row.key)}>
                          Editar
                        </Button>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            );
          })
        )}
      </div>

      <Dialog open={formOpen} onOpenChange={setFormOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>{rows.some((r) => r.key === editKey.trim()) ? "Editar variable" : "Nueva variable"}</DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <Label className="text-[11px]">Nombre</Label>
              <Input className="mt-0.5 h-8 font-mono text-xs" value={editKey} onChange={(e) => setEditKey(e.target.value)} autoComplete="off" />
            </div>
            <div>
              <Label className="text-[11px]">Nuevo valor</Label>
              <Input
                className="mt-0.5 h-8 font-mono text-xs"
                type="password"
                value={editValue}
                onChange={(e) => setEditValue(e.target.value)}
                placeholder="••••••••"
                autoComplete="off"
              />
            </div>
            <div>
              <Label className="text-[11px]">Contexto</Label>
              <select
                className="mt-0.5 h-8 w-full rounded-md border border-input bg-background px-2 text-xs"
                value={context}
                onChange={(e) => setContext(e.target.value)}
              >
                <option value="production">Production</option>
                <option value="deploy-preview">Deploy Preview</option>
                <option value="branch-deploys">Branch deploy</option>
                <option value="dev">Dev</option>
                <option value="all">Todos</option>
              </select>
            </div>
          </div>
          <DialogFooter className="flex-wrap gap-2">
            <Button
              size="sm"
              variant="outline"
              disabled={busy || !editKey.trim() || !rows.some((r) => r.key === editKey.trim())}
              onClick={() => void run("delete", true)}
            >
              Eliminar
            </Button>
            <Button
              size="sm"
              disabled={busy}
              onClick={() => void run(rows.some((r) => r.key === editKey.trim()) ? "update" : "create")}
            >
              {busy ? <Loader2 className="mr-2 h-3.5 w-3.5 animate-spin" /> : null}
              Guardar en Netlify
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <AlertDialog open={confirmOpen} onOpenChange={setConfirmOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Estás modificando una variable sensible</AlertDialogTitle>
            <AlertDialogDescription>
              El cambio puede afectar el funcionamiento de producción. El nuevo valor quedará disponible para el próximo deploy.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancelar</AlertDialogCancel>
            <AlertDialogAction onClick={() => void run(pendingAction, true)}>Confirmar cambio</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </AdminGlassPanel>
  );
}
